import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import styled from '@emotion/styled';
import { extractBrandInsightsIds, isEverythingContext, type BrandInsightsPageContext } from '@trinity/chat';
import type { Segments } from '@trinity/chat/api/interfaces/projects';
import {
  ChatLandingCardsVisibleContext,
  ChatPageContextDataContext,
} from '@trinity/chat-components/components/Chat/context';
import { ChatModal } from '@trinity/chat-components/components/Chat.Modal';
import {
  ChatSidePanel,
  getSentimentDefaultPanelWidth,
  MainArea,
  SENTIMENT_POPOVER_MIN_VIEWPORT,
  SidePanelArea,
  SidePanelLayout,
  SidePanelToggleContext,
  SidePanelHeightAnchorRefContext,
} from '@trinity/chat-components/components/Chat.SidePanel';
import { ChatStateContainer } from '@trinity/chat-components/components/Chat.State/Chat.State';
import { ChatWidget } from '@trinity/chat-components/components/Chat.Widget';
import { useLocation } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { QueryDownloaderContainer } from '@trinity/chat-components/containers/QueryDownloader/QueryDownloader.Container';
import { AuraChatContainer } from './AuraChat.Container';
import { ChatContainer } from './Chat.Container';
import { BrandInsightsModalContext, ChatViewType, ChatViewTypeContext } from './context';
import { useContextResetOnNavigation } from './hooks/useContextResetOnNavigation';
import { useSegmentContextResetOnProjectRoot } from './hooks/useSegmentContextResetOnProjectRoot';
import { LinkHashContainer, LinkStateContainer, useBrandInsightsHash } from './Link.Container';
import { SegmentChatContainer } from './SegmentChat.Container';
import { SegmentIconCacheLoader } from './SegmentIconCacheLoader';
import {
  hasClosedSidePanelOpenStateMatching,
  readSidePanelOpenState,
  writeSidePanelOpenState,
  type SidePanelOpenState,
} from './side-panel-open-state';
import type { ChatRoutePolicy, ChatSidePanelRouteAdapter } from './side-panel-route-policy';
import { SidePanelPendingPlaceholder } from './SidePanelHost.Placeholder';

type Props = {
  children: ReactNode;
  // Rendered outside MainArea so it always spans the full viewport width and is never
  // narrowed by the side panel — only `children` (the routed page content) narrows.
  header?: ReactNode;
  // How the chat is offered on the current route, resolved by the host app from its own route
  // table. Passed in already resolved so an app can layer its own rules on top (e.g. by role).
  policy: ChatRoutePolicy;
  // The route knowledge that depends on the app's URL scheme. Pass a stable reference.
  routeAdapter: ChatSidePanelRouteAdapter;
  canShowWidget: boolean;
  // Chat access + session ready, independent of `contextData` (set async by the routed page).
  // Gates the eager open and chat-tree mount below.
  canMountChat: boolean;
  // A page is hiding chat without navigating (e.g. the Digital Twin tab), so there's no context
  // to wait for.
  isChatSuppressedByPage?: boolean;
  // The routed page's code is still loading (Suspense fallback showing).
  isRouteLoading?: boolean;
  // Hides the lightbulb without affecting the rest of the route policy.
  widgetSuppressed?: boolean;
  themeVersion?: 'v1' | 'v2';
  // Whether the routed page scrolls inside the host's main area (the default) or with the window.
  // Pages built to scroll with the window (and use `position: sticky` against the viewport) pass
  // `false`.
  mainAreaScrolls?: boolean;
  // Set on the wrapping element only while the panel is open and mounted, for app styles that key
  // off the panel being open.
  rootId?: string;
  fetchProjectSegments: (
    data: Segments.GetSegmentsByProjectId.Request
  ) => Promise<Segments.GetSegmentsByProjectId.Response>;
};

/**
 * Null-rendering component placed inside ChatStateContainer so it has access to
 * both ChatStateDispatchContext and ChatPageContextDataContext for reset logic.
 */
const ContextResetWatcher = ({
  getNavigationBasePath,
}: Pick<ChatSidePanelRouteAdapter, 'getNavigationBasePath'>) => {
  useContextResetOnNavigation({ getNavigationBasePath });
  return null;
};

/**
 * Null-rendering component that clears segment-scoped chat state on project-root navigation.
 */
const SegmentContextResetWatcher = ({
  isSegmentsScopePath,
}: Pick<ChatSidePanelRouteAdapter, 'isSegmentsScopePath'>) => {
  useSegmentContextResetOnProjectRoot({ isSegmentsScopePath });
  return null;
};

const shouldUseModalByViewport = (): boolean => {
  return window.innerWidth < SENTIMENT_POPOVER_MIN_VIEWPORT;
};

// The `route:` part of the scope key (see `currentScopeKey` in the host).
const resolveScopeRouteBucket = (routeAdapter: ChatSidePanelRouteAdapter, pathname: string): string => {
  return routeAdapter.getScopeRouteBucket(pathname) ?? pathname.split(/[?#]/)[0] ?? pathname;
};

// How long to hold the panel on a neutral placeholder (instead of closing it) waiting for the
// routed page's own `contextData` before giving up and revealing the chat tree as-is. Bounds
// the wait for routes/entities where no context ever arrives (e.g. a project without insights
// chat enabled), which would otherwise leave the placeholder showing forever.
const CHAT_CONTEXT_GRACE_MS = 4000;

type ChatContextWaitInput = {
  scopeKey: string;
  pathname: string;
  routeAdapter: ChatSidePanelRouteAdapter;
  contextData: BrandInsightsPageContext.Item;
  isPanelOpen: boolean;
  canShowWidget: boolean;
  isRouteLoading: boolean;
  // The route can't host the panel, or the page suppresses it: no context will arrive to wait for.
  isPanelBlocked: boolean;
};

type ChatContextWait = {
  // On a project route the global context is the projects list's, still registered on the first
  // render after navigating; it isn't this page's context, so keep waiting for the real one.
  isStaleGlobalContext: boolean;
  // The wait for the page's `contextData` has run out.
  hasGraceElapsed: boolean;
  // The panel is open on a placeholder, waiting for the page's `contextData`.
  isAwaiting: boolean;
};

// Holds the panel on a placeholder, rather than closing it, while the page's chat context arrives.
// The grace period is reset per `scopeKey`, so every navigation gets its own, not just the first page.
const useChatContextWait = ({
  scopeKey,
  pathname,
  routeAdapter,
  contextData,
  isPanelOpen,
  canShowWidget,
  isRouteLoading,
  isPanelBlocked,
}: ChatContextWaitInput): ChatContextWait => {
  const isStaleGlobalContext =
    !!contextData && routeAdapter.isProjectRoutePath(pathname) && isEverythingContext(contextData);
  const [graceScopeKey, setGraceScopeKey] = useState(scopeKey);
  const [hasGraceElapsed, setHasGraceElapsed] = useState(false);
  if (scopeKey !== graceScopeKey) {
    setGraceScopeKey(scopeKey);
    setHasGraceElapsed(false);
  }
  useEffect(() => {
    // Don't spend the wait on the route's code still downloading (slow on a cold dev server).
    if (!isPanelOpen || (canShowWidget && !isStaleGlobalContext) || hasGraceElapsed || isRouteLoading) {
      return;
    }
    const timeoutId = window.setTimeout(() => setHasGraceElapsed(true), CHAT_CONTEXT_GRACE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [canShowWidget, hasGraceElapsed, isPanelOpen, isRouteLoading, isStaleGlobalContext]);

  const isAwaiting =
    isPanelOpen && (!canShowWidget || isStaleGlobalContext) && !hasGraceElapsed && !isPanelBlocked;

  return { isStaleGlobalContext, hasGraceElapsed, isAwaiting };
};

// Whether the wait for the page's context is shown as a whole-panel placeholder. Only until the real
// panel has been shown once: until then there is nothing on screen to keep. After it the panel stays
// mounted across the wait and only its chat area shows a skeleton, so its header and view state
// don't reset on every navigation.
const useShowsPendingPlaceholder = (shouldMountChat: boolean, isAwaitingChatContext: boolean): boolean => {
  const [hasShownChatPanel, setHasShownChatPanel] = useState(false);
  if (!hasShownChatPanel && shouldMountChat && !isAwaitingChatContext) {
    setHasShownChatPanel(true);
  }

  return isAwaitingChatContext && !hasShownChatPanel;
};

type ReserveOnNavigationInput = {
  pathname: string;
  routeAdapter: ChatSidePanelRouteAdapter;
  isPanelOpen: boolean;
  isModalOpen: boolean;
  canMountChat: boolean;
  shouldAutoOpen: boolean;
  isPanelBlocked: boolean;
  reservePanel: () => void;
};

// Reserves the panel's space as soon as we navigate to a route that auto-opens it, instead of
// waiting for the page's context: the panel then appears together with the page rather than a
// beat after it. It shows the placeholder until the context arrives.
const useReservePanelOnNavigation = ({
  pathname,
  routeAdapter,
  isPanelOpen,
  isModalOpen,
  canMountChat,
  shouldAutoOpen,
  isPanelBlocked,
  reservePanel,
}: ReserveOnNavigationInput): void => {
  const [reservedForPathname, setReservedForPathname] = useState(pathname);
  if (pathname === reservedForPathname) {
    return;
  }
  setReservedForPathname(pathname);
  if (
    !isPanelOpen &&
    !isModalOpen &&
    canMountChat &&
    shouldAutoOpen &&
    !isPanelBlocked &&
    !shouldUseModalByViewport() &&
    // `currentScopeKey` still describes the previous page here, so look the preference up by route.
    !hasClosedSidePanelOpenStateMatching(`|route:${resolveScopeRouteBucket(routeAdapter, pathname)}|`)
  ) {
    reservePanel();
  }
};

export const ChatSidePanelHost = ({
  children,
  header,
  policy,
  routeAdapter,
  canShowWidget,
  canMountChat,
  isChatSuppressedByPage = false,
  isRouteLoading = false,
  widgetSuppressed = false,
  themeVersion = 'v1',
  mainAreaScrolls = true,
  rootId,
  fetchProjectSegments,
}: Props) => {
  const hashValue = useBrandInsightsHash();
  const { pathname } = useLocation();
  const [isModalOpen, handleToggleModal] = useContext(BrandInsightsModalContext);
  const anchorRef = useContext(SidePanelHeightAnchorRefContext);
  const contextData = useContext(ChatPageContextDataContext);
  const currentScopeKey = useMemo(() => {
    const routeFileMatch = pathname.match(/\/files\/(\d+)/);
    const ids = contextData ? extractBrandInsightsIds(contextData) : undefined;
    const projectId = ids?.projectId ?? null;
    const fileId = routeFileMatch?.[1] ?? null;
    const routeBucket = resolveScopeRouteBucket(routeAdapter, pathname);

    return [
      `project:${projectId ?? 'global'}`,
      `file:${fileId ?? 'none'}`,
      `route:${routeBucket}`,
      `context:${contextData?.type ?? 'none'}`,
    ].join('|');
  }, [contextData, pathname, routeAdapter]);
  const isSidePanelChatDisabled = policy.presentationMode === 'disabled';
  const isChatWidgetHidden = policy.presentationMode === 'hidden';
  const isModalChatRoute = policy.presentationMode === 'modal';
  // Only routes whose policy opts in to auto-open (each app's own route policy decides, e.g. project
  // tabs, file tabs and standalone patient-journey routes). Within each opted-in group, tab switches
  // keep the same scope classification, so route-only navigation doesn't cause auto-open churn.
  const shouldAutoOpenSidePanelChat = policy.autoOpen;

  // Browser session storage-driven override for side panel open/close state, scoped to
  // `currentScopeKey`. Reloaded during render (not in an effect) when the scope changes.
  const [sidePanelOpenStateScopeKey, setSidePanelOpenStateScopeKey] = useState(currentScopeKey);
  const [sidePanelOpenState, setSidePanelOpenState] = useState<SidePanelOpenState | null>(() => {
    return readSidePanelOpenState(currentScopeKey);
  });
  // `currentScopeKey` folds in ids extracted from `contextData`, so it's still provisional
  // (e.g. `project:global|...`) on the render(s) before that resolves — reconciled below, once
  // `isPanelOpen` exists, rather than here, so a stored 'closed' preference under the resolved
  // key can override an eager open that was based on the provisional one.
  const hasScopeKeyChanged = currentScopeKey !== sidePanelOpenStateScopeKey;

  // Eagerly reserve the side panel's layout space on first render when every synchronous
  // signal (route policy, viewport, prior session choice) already points to auto-open, rather
  // than starting closed and waiting on `canShowWidget`/`contextData` — set async by the routed
  // page — to flip it open a beat later. That gap is what previously made the main content
  // render full-width and then get pushed aside shortly after initial load. Only runs once,
  // on mount; later navigations are handled by the auto-open effect below as before.
  const [isPanelOpen, setIsPanelOpen] = useState(() => {
    if (
      !canMountChat ||
      !shouldAutoOpenSidePanelChat ||
      isSidePanelChatDisabled ||
      isChatWidgetHidden ||
      isModalChatRoute ||
      shouldUseModalByViewport()
    ) {
      return false;
    }

    return readSidePanelOpenState(currentScopeKey) !== 'closed';
  });

  if (hasScopeKeyChanged) {
    setSidePanelOpenStateScopeKey(currentScopeKey);
    const resolvedSidePanelOpenState = readSidePanelOpenState(currentScopeKey);
    setSidePanelOpenState(resolvedSidePanelOpenState);
    // The eager open above (and the auto-open effect, mid-navigation) can open the panel using
    // a provisional/stale scope key before this one resolves — if the resolved key's own stored
    // preference is 'closed', enforce it now instead of leaving a stale open panel in place.
    if (resolvedSidePanelOpenState === 'closed' && isPanelOpen) {
      setIsPanelOpen(false);
    }
  }
  // Lazy-mount: only mount the chat provider tree once the user has opened the panel
  // or modal. Once mounted, keep it alive so the session persists across close/reopen.
  // Gated on `canMountChat` (not the stricter `canShowWidget`): the panel measures its own
  // width from its mounted content (see SidePanelArea), so waiting on `canShowWidget` — which
  // also waits on `contextData`, set async by the routed page — left it rendering at 0 width
  // briefly. `canMountChat` still requires a real chat session/access, so this never mounts
  // with GroupFeaturesContext etc. unavailable.
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  // Latch the first time the chat is actually mounted open, however it got opened (the first-render
  // eager open, a narrow-viewport hand-off to the modal, a deep link) — not only through the paths
  // below that set it explicitly — so closing it never throws the session away.
  if (!hasBeenOpened && (isPanelOpen || isModalOpen) && canMountChat) {
    setHasBeenOpened(true);
  }
  // A chat link in the URL (`#bi:ocid:`) mounts it too: `LinkHashContainer`, which opens the modal
  // for that link, lives inside the chat tree, so without this a link opened on a route where the
  // panel does not open on its own (a modal-only route, a narrow viewport) would do nothing. Not on
  // a route that hides the chat, which has an interface of its own.
  const hasChatLink = !!hashValue && !isChatWidgetHidden;
  const shouldMountChat = (hasBeenOpened || isPanelOpen || isModalOpen || hasChatLink) && canMountChat;
  const [sidePanelHeight, setSidePanelHeight] = useState<string | undefined>(undefined);

  // If the panel is open but `contextData` isn't (yet) set for the current scope — whether on
  // initial load, or because navigating here unmounted whatever previously held the page's chat
  // context before this scope's own registration effect had a chance to run — show a placeholder
  // (same footprint, no content) and hold off closing, rather than visibly closing and reopening
  // the panel once the new context lands. Reset per `currentScopeKey` so every navigation gets
  // its own grace window, not just the first page. `hasChatContextGraceElapsed` bounds the wait
  // for scopes that never register a context at all (e.g. a project without insights chat
  // enabled) so the panel doesn't sit on a placeholder — or stay open — forever.
  // No placeholder where no context will arrive; the render-phase close below handles those.
  const routeCanHostSidePanel = !isSidePanelChatDisabled && !isChatWidgetHidden && !isModalChatRoute;
  const isPanelBlocked = !routeCanHostSidePanel || isChatSuppressedByPage;
  const {
    isStaleGlobalContext,
    hasGraceElapsed: hasChatContextGraceElapsed,
    isAwaiting: isAwaitingChatContext,
  } = useChatContextWait({
    scopeKey: currentScopeKey,
    pathname,
    routeAdapter,
    contextData,
    isPanelOpen,
    canShowWidget,
    isRouteLoading,
    isPanelBlocked,
  });

  const showsPendingPlaceholder = useShowsPendingPlaceholder(shouldMountChat, isAwaitingChatContext);

  // Close the panel on routes that can't host it, or when the page suppresses chat.
  // Done during render so the open panel is never painted (an effect left it open for a frame).
  if (isPanelOpen && isPanelBlocked) {
    setIsPanelOpen(false);
  }

  useReservePanelOnNavigation({
    pathname,
    routeAdapter,
    isPanelOpen,
    isModalOpen,
    canMountChat,
    shouldAutoOpen: shouldAutoOpenSidePanelChat,
    isPanelBlocked,
    reservePanel: () => {
      setHasBeenOpened(true);
      setIsPanelOpen(true);
    },
  });

  const openPanel = useCallback(() => {
    if (!canShowWidget) {
      return;
    }

    setHasBeenOpened(true);
    // Any explicit user open (panel or modal) should clear a prior "closed" override for this scope.
    setSidePanelOpenState('open');
    writeSidePanelOpenState('open', currentScopeKey);

    if (isSidePanelChatDisabled || isModalChatRoute || shouldUseModalByViewport()) {
      handleToggleModal();
    } else {
      setIsPanelOpen(true);
    }
  }, [canShowWidget, currentScopeKey, handleToggleModal, isModalChatRoute, isSidePanelChatDisabled]);

  const lastClosedAtRef = useRef<number | null>(null);

  const closePanel = useCallback(() => {
    lastClosedAtRef.current = Date.now();
    setSidePanelOpenState('closed');
    writeSidePanelOpenState('closed', currentScopeKey);
    setIsPanelOpen(false);
  }, [currentScopeKey]);

  // Dynamically switch between side panel and modal when the viewport crosses the
  // breakpoint while chat is already open, so the user never loses access to their
  // session mid-resize.
  //
  // Narrow (< SENTIMENT_POPOVER_MIN_VIEWPORT) with panel open → close panel, open modal
  // Wide  (>= SENTIMENT_POPOVER_MIN_VIEWPORT) with modal open → close modal, open panel
  //
  // No-op when chat is fully closed, or when the user lacks side-panel access.
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${SENTIMENT_POPOVER_MIN_VIEWPORT - 1}px)`);
    const handleChange = (e: MediaQueryListEvent) => {
      if (!canShowWidget) {
        if (isPanelOpen) {
          setIsPanelOpen(false);
        }
        if (isModalOpen) {
          handleToggleModal(false);
        }
        return;
      }

      if (e.matches && isPanelOpen) {
        // Viewport narrowed below breakpoint while panel is open – switch to modal
        setIsPanelOpen(false);
        handleToggleModal(true);
      } else if (
        !e.matches &&
        isModalOpen &&
        !isSidePanelChatDisabled &&
        !isChatWidgetHidden &&
        !isModalChatRoute
      ) {
        // Viewport widened above breakpoint while modal is open – switch to panel
        handleToggleModal(false); // close modal
        // The narrow-viewport handoff may have persisted 'closed' (the panel's resize fallback
        // goes through closePanel). That was not a user dismissal, so clear it; otherwise the
        // panel re-reads it, stays dismissed, and the light bulb stays hidden.
        setSidePanelOpenState('open');
        writeSidePanelOpenState('open', currentScopeKey);
        setIsPanelOpen(true);
      } else {
        // The breakpoint was crossed with nothing open to hand over, or the route can't show a panel.
      }
    };
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, [
    currentScopeKey,
    canShowWidget,
    isPanelOpen,
    isModalOpen,
    handleToggleModal,
    isSidePanelChatDisabled,
    isChatWidgetHidden,
    isModalChatRoute,
  ]);

  // Auto-open side panel when enabled for this route.
  // Only runs when pathname changes (route navigation), not on state updates.
  // Early-return check for isPanelOpen prevents duplicate opens without re-run dependency.
  // If route policy changes while panel is open, the render-phase close above handles cleanup.

  const prevScopeKeyRef = useRef<string | null>(null);

  // Forget the last auto-opened scope so returning to it auto-opens again (see Condition 4).
  useEffect(() => {
    if (isPanelBlocked) {
      prevScopeKeyRef.current = null;
    }
  }, [isPanelBlocked]);

  // Unified auto-open effect: combines all guards from previous dual-effect implementation.
  // Fires only once per dependency change to prevent race conditions that caused flickering.
  // GATE: mainContentLoaded ensures we don't auto-open until the page is fully loaded, the widget
  // is available, and the modal is not already open (to prevent flicker and unwanted re-opens).
  const mainContentLoaded = useMemo(() => {
    // Consider content loaded and ready once we have context data AND the widget is available
    // AND the modal is not currently open. This prevents the auto-open effect from scheduling
    // opens when canShowWidget is false or when the modal is already open.
    // TODO: insert override from session storage
    return !!contextData && !isStaleGlobalContext && canShowWidget && !isModalOpen;
  }, [contextData, isStaleGlobalContext, canShowWidget, isModalOpen]);

  useEffect(() => {
    // GATE: Don't auto-open until main content is loaded and stable
    if (!mainContentLoaded) {
      return;
    }

    // Condition 0: Only auto-open on routes explicitly opted into this behavior
    // (see shouldAutoOpenSidePanelChatForPath in side-panel-chat-policy.ts for the current allow-list).
    if (!shouldAutoOpenSidePanelChat) {
      return;
    }

    // Respect explicit user close intent for this browser session.
    if (sidePanelOpenState === 'closed') {
      return;
    }

    // Condition 1: Check if side panel is available for this route
    if (isSidePanelChatDisabled || isChatWidgetHidden || isModalChatRoute || shouldUseModalByViewport()) {
      return;
    }

    // Condition 2: Don't auto-open if panel is already open
    if (isPanelOpen) {
      prevScopeKeyRef.current = currentScopeKey;
      return;
    }

    // Condition 3: Don't auto-open if user just manually closed the panel (within 500ms)
    const timeSinceClose = lastClosedAtRef.current ? Date.now() - lastClosedAtRef.current : Infinity;
    if (timeSinceClose < 500) {
      return;
    }

    // Condition 4: Do not auto-open when the current entity scope is unchanged.
    if (prevScopeKeyRef.current === currentScopeKey && prevScopeKeyRef.current !== null) {
      return;
    }

    // All guards passed — auto-open the panel for this new scope
    const timeoutId = window.setTimeout(() => {
      setHasBeenOpened(true);
      setIsPanelOpen(true);
    }, 0);

    prevScopeKeyRef.current = currentScopeKey;
    return () => window.clearTimeout(timeoutId);
  }, [
    currentScopeKey,
    isChatWidgetHidden,
    isModalChatRoute,
    isSidePanelChatDisabled,
    isPanelOpen,
    mainContentLoaded,
    sidePanelOpenState,
    shouldAutoOpenSidePanelChat,
  ]);

  // Clear close intent when user navigates to a different scope, but only if the
  // auto-open effect already ran (mainContentLoaded is true). If mainContentLoaded
  // is still false, let the auto-open effect handle prevScopeKeyRef updates on the
  // next mainContentLoaded transition to avoid marking the scope as "seen" too early.
  useEffect(() => {
    if (!mainContentLoaded) {
      return;
    }
    lastClosedAtRef.current = null;
  }, [currentScopeKey, mainContentLoaded]);

  // Tracks whether `canShowWidget` has ever resolved true, so the optimistic pre-open above
  // isn't immediately collapsed by the effect below while `contextData` is still loading.
  const hasConfirmedWidgetAvailableRef = useRef(false);
  useEffect(() => {
    if (canShowWidget) {
      hasConfirmedWidgetAvailableRef.current = true;
    }
  }, [canShowWidget]);

  useEffect(() => {
    if (
      canShowWidget ||
      (!isPanelOpen && !isModalOpen) ||
      (!hasConfirmedWidgetAvailableRef.current && !hasChatContextGraceElapsed) ||
      isAwaitingChatContext
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (isPanelOpen) {
        setIsPanelOpen(false);
      }
      if (isModalOpen) {
        handleToggleModal();
      }
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [
    canShowWidget,
    handleToggleModal,
    hasChatContextGraceElapsed,
    isAwaitingChatContext,
    isModalOpen,
    isPanelOpen,
  ]);

  useEffect(() => {
    const getAnchorElement = () => {
      // Prefer ref if available via Context
      if (anchorRef?.current) {
        return anchorRef.current;
      }
    };

    const updateSidePanelHeight = () => {
      const anchor = getAnchorElement();
      if (!anchor) {
        setSidePanelHeight(undefined);
        return;
      }

      const height = Math.round(anchor.getBoundingClientRect().height);
      if (height <= 0) {
        setSidePanelHeight(undefined);
        return;
      }

      const nextHeight = `${height}px`;
      setSidePanelHeight((current) => (current === nextHeight ? current : nextHeight));
    };

    updateSidePanelHeight();

    const anchor = getAnchorElement();
    const canObserveResize = typeof ResizeObserver !== 'undefined';
    const resizeObserver =
      anchor && canObserveResize ? new ResizeObserver(() => updateSidePanelHeight()) : null;
    if (anchor && resizeObserver) {
      resizeObserver.observe(anchor);
    }

    window.addEventListener('resize', updateSidePanelHeight);

    return () => {
      window.removeEventListener('resize', updateSidePanelHeight);
      resizeObserver?.disconnect();
    };
  }, [pathname, anchorRef]);

  const sidePanelToggleContextValue = useMemo(() => {
    return {
      isOpen: isPanelOpen,
      openPanel,
      closePanel,
      scopeKey: currentScopeKey,
    };
  }, [closePanel, currentScopeKey, isPanelOpen, openPanel]);

  return (
    <div id={isPanelOpen && shouldMountChat && canShowWidget ? rootId : undefined}>
      <SidePanelToggleContext.Provider value={sidePanelToggleContextValue}>
        {header}

        <SidePanelLayout themeVersion={themeVersion} isPanelOpen={isPanelOpen}>
          <MainLayout themeVersion={themeVersion}>
            <MainArea isOpen={isPanelOpen} scrolls={mainAreaScrolls}>
              {children}
            </MainArea>
          </MainLayout>
          <SidePanelArea
            isOpen={isPanelOpen}
            panelHeight={sidePanelHeight}
            themeVersion={themeVersion}
            instant={isPanelBlocked || isAwaitingChatContext}
          >
            {shouldMountChat && (
              <ChatStateContainer>
                <LinkStateContainer initialChatIdentifier={hashValue}>
                  <LinkHashContainer>
                    <AuraChatContainer canToggleAuraChat={false}>
                      <SegmentChatContainer>
                        <SegmentIconCacheLoader fetchProjectSegments={fetchProjectSegments} />
                        <ContextResetWatcher getNavigationBasePath={routeAdapter.getNavigationBasePath} />
                        <SegmentContextResetWatcher isSegmentsScopePath={routeAdapter.isSegmentsScopePath} />
                        <ChatViewTypeContext.Provider value={ChatViewType.Modal}>
                          <QueryDownloaderContainer>
                            <ChatContainer>
                              <ChatLandingCardsVisibleContext.Provider value={false}>
                                {showsPendingPlaceholder ? (
                                  <SidePanelPendingPlaceholder
                                    width={getSentimentDefaultPanelWidth(window.innerWidth)}
                                    themeVersion={themeVersion}
                                  />
                                ) : (
                                  <ChatSidePanel
                                    responsiveWidthMode="sentiment"
                                    themeVersion={themeVersion}
                                    isLoading={isAwaitingChatContext}
                                  />
                                )}
                                {isModalOpen && <ChatModal onClose={handleToggleModal} open={isModalOpen} />}
                              </ChatLandingCardsVisibleContext.Provider>
                            </ChatContainer>
                          </QueryDownloaderContainer>
                        </ChatViewTypeContext.Provider>
                      </SegmentChatContainer>
                    </AuraChatContainer>
                  </LinkHashContainer>
                </LinkStateContainer>
              </ChatStateContainer>
            )}
          </SidePanelArea>
        </SidePanelLayout>
        {canShowWidget && !isPanelOpen && !isModalOpen && !widgetSuppressed && !isChatWidgetHidden && (
          <ChatWidget themeVersion={themeVersion} onClick={openPanel} />
        )}
      </SidePanelToggleContext.Provider>
    </div>
  );
};

type MainLayoutProps = {
  children: React.ReactNode;
  themeVersion?: 'v1' | 'v2';
};

const MainLayout = ({ children, themeVersion = 'v1' }: MainLayoutProps) => {
  if (themeVersion === 'v2') {
    return (
      <MainRoot>
        <MainWrap>{children}</MainWrap>
      </MainRoot>
    );
  }

  return <>{children}</>;
};

const MainRoot = styled.div`
  display: flex;
  align-items: stretch;
  min-width: 0;
  width: 100%;
  padding: 15px 15px 0;
  scrollbar-gutter: stable;
`;

const MainWrap = styled.div`
  border: 1px solid var(--sec-01);
  border-radius: 10px;
  display: flex;
  align-items: stretch;
  min-width: 0;
  width: 100%;
  box-shadow:
    0 1px 2px rgba(16, 24, 40, 0.04),
    0 4px 12px rgba(16, 24, 40, 0.06),
    0 16px 40px -4px rgba(16, 24, 40, 0.08);
  background-color: var(--pri-01);
  height: calc(100vh - (var(--navbar-height, 60px) + 30px));
  overflow: hidden;
`;
